import React, { useCallback, useMemo, memo, useEffect, useRef } from 'react';
import { useAuthNavigation } from '../../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, Platform, Image, type TextStyle } from 'react-native';
import { Clickable2 } from '../../../components/common/Clickable2';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { useTranslation, getApiText, formatNumber } from '../../../shared/i18n';
import { useRtl } from '../../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../../shared/i18n/rtl';

import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withSequence,
  withTiming,
  cancelAnimation,
  interpolate,
  interpolateColor,
  Extrapolation,
} from 'react-native-reanimated';

import { useThemeColors, useThemeStore, type ThemeColors } from '../../../shared/theme';
import { hexToRgba } from '../../../shared/theme/colorUtils';
import { triggerMenuItemHaptic } from '../../components/tactile';
import type { MenuFlowItemCardProps } from '../../components/MenuFlowItemCard';
import {
  MENU_FLOW_ITEM_BADGE_INSET,
  MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT,
  MENU_FLOW_ITEM_CARD_HEIGHT,
  MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
  MENU_FLOW_ITEM_CONTENT_PADDING_H,
  MENU_FLOW_ITEM_CONTENT_PADDING_V,
  MENU_FLOW_ITEM_CONTENT_PADDING_TOP,
  MENU_FLOW_ITEM_DESC_FONT_SIZE,
  MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS,
  MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
  MENU_FLOW_ITEM_IMAGE_GAP,
  MENU_FLOW_ITEM_IMAGE_SIZE,
  MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH,
  MENU_FLOW_ITEM_TITLE_FONT_SIZE,
  MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
} from './menuFlowItemCardLayout';
import { studioCardShell } from '../../../shared/theme/studio/dashboardConstants';

const IMAGE_SECTION_WIDTH = MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH;
const CARD_SPRING = { stiffness: 280, damping: 44, mass: 0.55 };
const MY_SPRING = { stiffness: 800, damping: 20, mass: 1.5 };
const INTERACTION_COOLDOWN_MS = 140;
const STEP_HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 };
const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

const AnimatedView = Animated.createAnimatedComponent(View);

function createMenuFlowItemCardStyles(c: ThemeColors) {
  return StyleSheet.create({
    fullCardContainer: {
      height: MENU_FLOW_ITEM_CARD_HEIGHT,
      minHeight: MENU_FLOW_ITEM_CARD_HEIGHT,
      marginVertical: MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
      width: '100%',
      flexGrow: 0,
      flexShrink: 0,
      // overflow: 'hidden',
      ...studioCardShell(c),
      ...horizontalLtrDirectionStyle,
    },
    cardTouchableRow: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'stretch',
      overflow: 'hidden',
      ...horizontalLtrDirectionStyle,
    },
    imageSection: {
      width: IMAGE_SECTION_WIDTH,
      alignSelf: 'stretch',
      paddingTop: MENU_FLOW_ITEM_IMAGE_GAP,
      paddingLeft: MENU_FLOW_ITEM_IMAGE_GAP,
      paddingBottom: MENU_FLOW_ITEM_IMAGE_GAP,
    },
    imageFrame: {
      width: MENU_FLOW_ITEM_IMAGE_SIZE,
      height: MENU_FLOW_ITEM_IMAGE_SIZE,
      flexShrink: 0,
      overflow: 'hidden',
      backgroundColor: hexToRgba(c.text, 0.05),
    },
    image: {
      width: '100%',
      height: '100%',
      resizeMode: 'cover',
    },
    imagePlaceholder: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.primaryUltra ?? hexToRgba(c.primary, 0.1),
    },
    badgeOverlay: {
      position: 'absolute',
      top: MENU_FLOW_ITEM_BADGE_INSET,
      left: MENU_FLOW_ITEM_BADGE_INSET,
      right: MENU_FLOW_ITEM_BADGE_INSET,
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 6,
    },
    badgeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      flexShrink: 1,
    },
    mealChip: {
      paddingHorizontal: 7,
      paddingVertical: 3,
      borderRadius: 8,
      backgroundColor: hexToRgba(c.primary, 0.9),
    },
    mealChipText: {
      fontSize: 9,
      fontWeight: '800',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: c.onPrimary,
      ...ANDROID_TEXT_COMPACT,
    },
    selectionChip: {
      paddingHorizontal: 7,
      paddingVertical: 3,
      borderRadius: 8,
      backgroundColor: c.primary,
    },
    healthBadge: {
      width: 18,
      height: 18,
      borderRadius: 9,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.error,
    },
    healthBadgeText: {
      fontSize: 11,
      fontWeight: '800',
      color: c.onError,
      ...ANDROID_TEXT_COMPACT,
    },
    detailHint: {
      position: 'absolute',
      end: 0,
      bottom: 0,
      width: 30,
      height: 30,
      borderTopStartRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(c.surface, 0.92),
    },
    contentSection: {
      flex: 1,
      minWidth: 0,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      paddingTop: MENU_FLOW_ITEM_CONTENT_PADDING_TOP,
      paddingBottom: MENU_FLOW_ITEM_CONTENT_PADDING_V,
      justifyContent: 'space-between',
    },
    textArea: {
      flex: 1,
      minHeight: 0,
      justifyContent: 'flex-start',
    },
    title: {
      fontSize: MENU_FLOW_ITEM_TITLE_FONT_SIZE,
      fontWeight: '700',
      lineHeight: MENU_FLOW_ITEM_TITLE_FONT_SIZE + 4,
      minHeight: MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
      color: c.text,
      width: '100%',
      ...ANDROID_TEXT_COMPACT,
    },
    description: {
      width: '100%',
      alignSelf: 'stretch',
      fontSize: MENU_FLOW_ITEM_DESC_FONT_SIZE,
      lineHeight: MENU_FLOW_ITEM_DESC_FONT_SIZE + 4,
      color: c.textSecondary,
      marginTop: 6,
      ...ANDROID_TEXT_COMPACT,
    },
    footerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      minHeight: MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT,
      paddingTop: 8,
      ...horizontalLtrDirectionStyle,
    },
    footerActionSlot: {
      position: 'relative',
      minWidth: 128,
      minHeight: 34,
      alignItems: 'flex-end',
      justifyContent: 'center',
    },
    addAffordance: {
      position: 'absolute',
      right: 0,
      flexDirection: 'row',
      justifyContent: 'space-around',
      alignItems: 'center',
      gap: 2,
      paddingHorizontal: 6,
      paddingVertical: 6,
      borderRadius: 999,
      borderWidth: StyleSheet.hairlineWidth,
    },
    addAffordanceText: {
      fontSize: 12,
      fontWeight: '700',
      ...ANDROID_TEXT_COMPACT,
      paddingHorizontal: 4,
    },
    addIconCircle: {
      width: 22,
      height: 22,
      borderRadius: 11,
      alignItems: 'center',
      justifyContent: 'center',
    },
    quantityCounter: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: 18,
      overflow: 'hidden',
      minHeight: 34,
    },
    quantityBtn: {
      width: 34,
      height: 34,
      alignItems: 'center',
      justifyContent: 'center',
    },
    quantityValue: {
      minWidth: 28,
      textAlign: 'center',
      fontSize: 15,
      fontWeight: '800',
      ...ANDROID_TEXT_COMPACT,
    },
    quantityBtnText: {
      fontSize: 18,
      fontWeight: '700',
      lineHeight: 20,
      ...ANDROID_TEXT_COMPACT,
    },
  });
}

function menuItemHealthFromFlags(is_allergy: boolean, is_dislike: boolean): 'none' | 'allergy' | 'dislike' {
  if (is_allergy) {
    return 'allergy';
  }
  if (is_dislike) {
    return 'dislike';
  }
  return 'none';
}

export const MenuFlowItemCardSet2: React.FC<MenuFlowItemCardProps> = memo(
  ({
    themeStyleKey: _themeStyleKey,
    item,
    menuBuilderId: menuBuilderIdProp,
    onQuantityChange,
    mealsCount,
    snacksCount,
    isForceFixedMenu = false,
    selectionDisabled = false,
    onSelectionDisabledInteraction,
    onItemPress,
    isGroupLimitReached,
    onLimitExceeded,
    reduceMotion: _reduceMotion = false,
  }) => {
    const navigation = useAuthNavigation();
    const image_uri = item.attachment ?? null;
    const { t } = useTranslation();
    const rtl = useRtl();
    const menu_builder_id =
      typeof menuBuilderIdProp === 'number' ? Number(menuBuilderIdProp) : Number(item.menu_builder_id ?? item.food_type_id ?? 0);
    const isSelected = item.is_selected || item.quantity > 0;
    const health_code = typeof (item as { health_code?: number }).health_code === 'number' ? (item as { health_code?: number }).health_code : 0;
    const is_allergy = health_code === 1 || health_code === 10;
    const is_dislike = health_code === 2 || health_code === 20;
    const is_meal = item.food_type_category?.toLowerCase() === 'meal';
    const health_haptic = menuItemHealthFromFlags(is_allergy, is_dislike);

    const theme_c = useThemeColors() ?? useThemeStore.getState().colors;
    const styles = useMemo(() => createMenuFlowItemCardStyles(theme_c), [theme_c]);

    const active_state = useSharedValue(isSelected ? 1 : 0);
    const border_state = useSharedValue(!isSelected ? 0 : is_allergy ? 2 : is_dislike ? 3 : 1);
    const shake_offset = useSharedValue(0);
    const content_width_ref = useRef(200);
    const touch_x_ref = useRef(0);
    const interaction_lock_ref = useRef(false);

    const run_guarded = useCallback((fn: () => void) => {
      if (interaction_lock_ref.current) return;
      interaction_lock_ref.current = true;
      fn();
      setTimeout(() => {
        interaction_lock_ref.current = false;
      }, INTERACTION_COOLDOWN_MS);
    }, []);

    useEffect(() => {
      active_state.value = withSpring(isSelected ? 1 : 0, CARD_SPRING);
      border_state.value = withSpring(!isSelected ? 0 : is_allergy ? 2 : is_dislike ? 3 : 1, CARD_SPRING);
    }, [isSelected, is_allergy, is_dislike, active_state, border_state]);

    const triggerShake = useCallback(
      (direction: 'left' | 'right') => {
        triggerMenuItemHaptic('shake', health_haptic);
        const offset = direction === 'left' ? -0.01 : 0.01;
        shake_offset.value = withSequence(
          withTiming(offset, { duration: 25 }),
          withTiming(-offset, { duration: 25 }),
          withTiming(offset, { duration: 25 }),
          withTiming(-offset, { duration: 25 }),
          withSpring(0, MY_SPRING),
        );
      },
      [shake_offset, health_haptic],
    );

    const wouldExceedLimit = useCallback(() => {
      const limit = is_meal ? mealsCount : snacksCount;
      return item.quantity >= limit || isGroupLimitReached === true;
    }, [item.quantity, is_meal, mealsCount, snacksCount, isGroupLimitReached]);

    const handleInteraction = useCallback(
      (touchX: number, layoutWidth: number) => {
        if (selectionDisabled) {
          onSelectionDisabledInteraction?.();
          return;
        }
        if (isForceFixedMenu) {
          return;
        }
        const direction = layoutWidth != null && touchX != null ? (touchX < layoutWidth / 3 ? 'left' : 'right') : 'right';

        if (item.quantity === 0) {
          if (wouldExceedLimit()) {
            cancelAnimation(active_state);
            active_state.value = withSpring(0, CARD_SPRING);
            triggerShake(direction);
            onLimitExceeded?.(is_meal ? t('menu.maxMealLimitReached') : t('menu.maxSnackLimitReached'));
            return;
          }
          triggerMenuItemHaptic('quantity', health_haptic);
          if (health_code === 0) {
            cancelAnimation(active_state);
            active_state.value = withSpring(1, CARD_SPRING);
            border_state.value = withSpring(is_allergy ? 2 : is_dislike ? 3 : 1, CARD_SPRING);
          }
          onQuantityChange(menu_builder_id, item.id, true);
        } else if (item.quantity === 1) {
          triggerMenuItemHaptic('quantity', health_haptic);
          cancelAnimation(active_state);
          active_state.value = withSpring(0, CARD_SPRING);
          border_state.value = withSpring(0, CARD_SPRING);
          onQuantityChange(menu_builder_id, item.id, false);
        } else if (wouldExceedLimit()) {
          triggerShake(direction);
        }
      },
      [
        item.quantity,
        selectionDisabled,
        onSelectionDisabledInteraction,
        isForceFixedMenu,
        wouldExceedLimit,
        active_state,
        border_state,
        onQuantityChange,
        onLimitExceeded,
        triggerShake,
        menu_builder_id,
        item.id,
        is_meal,
        t,
        health_haptic,
        health_code,
        is_allergy,
        is_dislike,
      ],
    );

    const handleImageTap = useCallback(() => {
      run_guarded(() => {
        if (selectionDisabled) {
          onSelectionDisabledInteraction?.();
          return;
        }
        if (onItemPress) {
          onItemPress(item);
        } else {
          navigation.navigate('FoodItemDetail', { item, imageUri: image_uri ?? undefined });
        }
      });
    }, [
      item,
      image_uri,
      selectionDisabled,
      onSelectionDisabledInteraction,
      onItemPress,
      navigation,
      run_guarded,
    ]);

    const handleCardPress = useCallback(() => {
      run_guarded(() => {
        handleInteraction(touch_x_ref.current, content_width_ref.current);
      });
    }, [handleInteraction, run_guarded]);

    const handlePlus = useCallback(() => {
      run_guarded(() => {
        if (selectionDisabled) {
          onSelectionDisabledInteraction?.();
          return;
        }
        if (isForceFixedMenu) {
          return;
        }
        if (wouldExceedLimit()) {
          triggerShake('right');
          onLimitExceeded?.(is_meal ? t('menu.maxMealLimitReached') : t('menu.maxSnackLimitReached'));
          return;
        }
        triggerMenuItemHaptic('quantity', health_haptic);
        if (item.quantity === 0) {
          const health_blocks = health_code !== 0;
          if (!health_blocks) {
            cancelAnimation(active_state);
            active_state.value = withSpring(1, CARD_SPRING);
            border_state.value = withSpring(is_allergy ? 2 : is_dislike ? 3 : 1, CARD_SPRING);
          }
        }
        onQuantityChange(menu_builder_id, item.id, true);
      });
    }, [
      selectionDisabled,
      onSelectionDisabledInteraction,
      isForceFixedMenu,
      wouldExceedLimit,
      menu_builder_id,
      item.id,
      item.quantity,
      is_meal,
      onQuantityChange,
      onLimitExceeded,
      triggerShake,
      t,
      health_haptic,
      health_code,
      is_allergy,
      is_dislike,
      active_state,
      border_state,
      run_guarded,
    ]);

    const handleMinus = useCallback(() => {
      run_guarded(() => {
        if (selectionDisabled) {
          onSelectionDisabledInteraction?.();
          return;
        }
        if (isForceFixedMenu) {
          return;
        }
        triggerMenuItemHaptic('quantity', health_haptic);
        if (item.quantity === 1) {
          cancelAnimation(active_state);
          active_state.value = withSpring(0, CARD_SPRING);
          border_state.value = withSpring(0, CARD_SPRING);
        }
        onQuantityChange(menu_builder_id, item.id, false);
      });
    }, [
      selectionDisabled,
      onSelectionDisabledInteraction,
      isForceFixedMenu,
      menu_builder_id,
      item.id,
      item.quantity,
      onQuantityChange,
      health_haptic,
      active_state,
      border_state,
      run_guarded,
    ]);

    const item_name = getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name;
    const item_desc =
      getApiText(item as unknown as Record<string, unknown>, 'description', 'a_description') ||
      item.description ||
      t('menu.freshlyPrepared');

    const meal_label = is_meal ? t('menu.meal') : t('menu.snack');
    const selection_n = Number(item.selection_number ?? 0);
    const show_selection_tag = is_meal && selection_n > 0;

    const color_surface = theme_c.surface;
    const color_primary = theme_c.primary;
    const color_error = theme_c.error;
    const color_warning = theme_c.warning;
    const color_surface_elevated = theme_c.surfaceElevated;
    const color_shadow = theme_c.shadowColor ?? theme_c.black;

    const cardChromeStyle = useAnimatedStyle(() => {
      'worklet';
      const shake = shake_offset.value;
      const selected_border = interpolateColor(
        border_state.value,
        [0, 1, 2, 3],
        [color_primary, color_primary, color_error, color_warning],
      );
      return {
        transform: [{ scaleX: 1 + shake }, { scaleY: 1 + shake }],
        borderRadius: interpolate(active_state.value, [0, 1], [24, 20]),
        /** Always reserve width; transparent when unselected so only selected cards show a border. */
        borderWidth: MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
        borderColor: interpolateColor(active_state.value, [0, 1], ['rgba(0,0,0,0)', selected_border]),
        backgroundColor: interpolateColor(active_state.value, [0, 1], [color_surface, color_surface_elevated]),
        // ...(Platform.OS === 'ios'
        //   ? {
        //       shadowColor: color_shadow,
        //       shadowOffset: { width: 0, height: interpolate(active_state.value, [0, 1], [1, 3]) },
        //       shadowOpacity: interpolate(active_state.value, [0, 1], [0.05, 0.12]),
        //       shadowRadius: interpolate(active_state.value, [0, 1], [4, 10]),
        //     }
        //   : { elevation: interpolate(active_state.value, [0, 1], [1, 3]) }),
      };
    });

    const imageFrameStyle = useAnimatedStyle(() => ({
      borderRadius: interpolate(active_state.value, [0, 1], [MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS, 16]),
    }));

    const selectionChipStyle = useAnimatedStyle(() => ({
      opacity: active_state.value,
      transform: [{ scale: interpolate(active_state.value, [0, 1], [0.85, 1], Extrapolation.CLAMP) }],
    }));

    const stepperStyle = useAnimatedStyle(() => ({
      opacity: interpolate(active_state.value, [0, 1], [0, 1], Extrapolation.CLAMP),
      transform: [{ scale: interpolate(active_state.value, [0.4, 1], [0.92, 1], Extrapolation.CLAMP) }],
    }));

    const addAffordanceStyle = useAnimatedStyle(() => ({
      opacity: interpolate(active_state.value, [0, 0.35], [1, 0], Extrapolation.CLAMP),
      transform: [{ scale: interpolate(active_state.value, [0, 0.35], [1, 0.94], Extrapolation.CLAMP) }],
    }));

    const renderStepper = () => (
      <View style={[styles.quantityCounter, { backgroundColor: theme_c.primary }]}>
        <Clickable2
          onPressIn={handleMinus}
          hitSlop={STEP_HIT_SLOP}
          accessibilityRole="button"
          accessibilityLabel={t('menu.decreaseQuantity')}
          style={styles.quantityBtn}
          haptics={false}>
          <Text style={[styles.quantityBtnText, { color: theme_c.onPrimary }]}>-</Text>
        </Clickable2>
        <Text style={[styles.quantityValue, { color: theme_c.onPrimary }]}>{formatNumber(item.quantity)}</Text>
        <Clickable2
          onPressIn={handlePlus}
          hitSlop={STEP_HIT_SLOP}
          accessibilityRole="button"
          accessibilityLabel={t('menu.increaseQuantity')}
          style={styles.quantityBtn}
          haptics={false}>
          <Text style={[styles.quantityBtnText, { color: theme_c.onPrimary }]}>+</Text>
        </Clickable2>
      </View>
    );

    return (
      <AnimatedView style={[styles.fullCardContainer, cardChromeStyle]}>
        <View style={styles.cardTouchableRow}>
          <Clickable2
            onPress={handleImageTap}
            style={styles.imageSection}
            accessibilityRole="button"
            accessibilityLabel={t('menu.openItemDetail')}
            haptics
            hapticType="soft">
            <AnimatedView style={[styles.imageFrame, imageFrameStyle]}>
              {image_uri ? (
                <Image source={{ uri: image_uri }} style={styles.image} fadeDuration={0} />
              ) : (
                <View style={styles.imagePlaceholder}>
                  <SvgIcon name="meal" size={32} color={theme_c.primary} />
                </View>
              )}
              <View style={styles.badgeOverlay} pointerEvents="none">
                <View style={styles.badgeRow}>
                  {health_code !== 0 ? (
                    <View
                      style={[
                        styles.healthBadge,
                        is_dislike && !is_allergy ? { backgroundColor: theme_c.warning } : null,
                      ]}>
                      <Text style={styles.healthBadgeText}>!</Text>
                    </View>
                  ) : null}
                  <View style={styles.mealChip}>
                    <Text style={styles.mealChipText} numberOfLines={1}>
                      {meal_label}
                    </Text>
                  </View>
                </View>
                {show_selection_tag ? (
                  <Animated.View style={[styles.selectionChip, selectionChipStyle]}>
                    <Text style={styles.mealChipText}>#{selection_n}</Text>
                  </Animated.View>
                ) : null}
              </View>
              <View style={styles.detailHint} pointerEvents="none">
                <SvgIcon name="expand" size={14} color={theme_c.textSecondary} />
              </View>
            </AnimatedView>
          </Clickable2>

          <Clickable2
            onPress={handleCardPress}
            style={styles.contentSection}
            accessibilityRole="button"
            accessibilityLabel={item_name}
            haptics={false}>
            <View
              style={styles.textArea}
              onLayout={e => {
                content_width_ref.current = e.nativeEvent.layout.width;
              }}
              onTouchStart={e => {
                touch_x_ref.current = e.nativeEvent.locationX;
              }}>
              <Text
                style={[styles.title, rtl.leadingMediaCopyTextStyle, { color: isSelected ? theme_c.primary : theme_c.text }]}
                numberOfLines={1}>
                {item_name}
              </Text>

              <Text style={[styles.description, rtl.leadingMediaCopyTextStyle]} numberOfLines={2}>
                {item_desc}
              </Text>
            </View>

            <View style={styles.footerRow}>
              <View style={styles.footerActionSlot}>
                <AnimatedView
                  style={[
                    styles.addAffordance,
                    addAffordanceStyle,
                    {
                      borderColor: hexToRgba(theme_c.primary, 0.28),
                      backgroundColor: hexToRgba(theme_c.primary, 0.08),
                    },
                  ]}
                  pointerEvents="none">
                  <View style={[styles.addIconCircle, { backgroundColor: theme_c.primary }]}>
                    <Text style={[styles.quantityBtnText, { color: theme_c.onPrimary, fontSize: 16 }]}>+</Text>
                  </View>
                  <Text style={[styles.addAffordanceText, { color: theme_c.primary }]}>{t('menu.dailyFlowAdd')}</Text>
                </AnimatedView>
                <AnimatedView pointerEvents={isSelected ? 'auto' : 'none'} style={stepperStyle}>
                  {renderStepper()}
                </AnimatedView>
              </View>
            </View>
          </Clickable2>
        </View>
      </AnimatedView>
    );
  },
  (prev, next) =>
    prev.themeStyleKey === next.themeStyleKey &&
    prev.menuBuilderId === next.menuBuilderId &&
    prev.item.id === next.item.id &&
    prev.item.quantity === next.item.quantity &&
    prev.item.is_selected === next.item.is_selected &&
    prev.item.selection_number === next.item.selection_number &&
    (prev.item as { scaled_carbs?: number }).scaled_carbs === (next.item as { scaled_carbs?: number }).scaled_carbs &&
    (prev.item as { health_code?: number }).health_code === (next.item as { health_code?: number }).health_code &&
    prev.selectionDisabled === next.selectionDisabled &&
    prev.isForceFixedMenu === next.isForceFixedMenu &&
    prev.isGroupLimitReached === next.isGroupLimitReached &&
    prev.mealsCount === next.mealsCount &&
    prev.snacksCount === next.snacksCount &&
    prev.reduceMotion === next.reduceMotion,
);

MenuFlowItemCardSet2.displayName = 'MenuFlowItemCardSet2';
